import { FullStoryEvent, TrackData } from '@fly/shared';
import { FullStory, init } from '@fullstory/browser';
import { PublicVarScope } from '@fullstory/snippet';
import log from 'loglevel';

export class FullStoryService {
  private static instance: FullStoryService;
  private initialized = false;
  private consentGiven = true;

  private constructor() {}

  public static getInstance(): FullStoryService {
    if (!FullStoryService.instance) {
      FullStoryService.instance = new FullStoryService();
    }
    return FullStoryService.instance;
  }

  public init(devMode = false): void {
    if (this.initialized || typeof window === 'undefined') return;

    if (!import.meta.env.VITE_FULL_STORY_ORG_ID) {
      log.warn('FullStory: Missing orgId, initialization skipped');
      return;
    }

    try {
      init({
        orgId: import.meta.env.VITE_FULL_STORY_ORG_ID,
        devMode,
        recordCrossDomainIFrames: false,
      });
      this.initialized = true;
    } catch (err) {
      log.error('FullStory initialization failed:', err);
    }
  }

  public setConsent(consent: boolean): void {
    this.consentGiven = consent;
    if (!consent && this.initialized) {
      FullStory.shutdown();
      this.initialized = false;
    }
  }

  public event(name: FullStoryEvent, data: TrackData): void {
    if (!this.initialized || !this.consentGiven) return;
    try {
      FullStory.event(name.toString(), {
        ...data,
        timestamp: Date.now(),
      });
    } catch (err) {
      log.error('FullStory event failed:', err);
    }
  }

  public isInitialized(): boolean {
    return this.initialized;
  }

  public trackFunnel(
    funnelName: string,
    step: number,
    stepName: string,
    extraData?: TrackData
  ): void {
    this.event(funnelName as unknown as FullStoryEvent, {
      step,
      step_name: stepName,
      funnel_id: funnelName,
      ...extraData,
    });
  }
  public setVars(namespace: PublicVarScope, data: TrackData): void {
    if (!this.initialized || !this.consentGiven) return;
    try {
      FullStory.setVars(namespace, data);
    } catch (err) {
      log.error('FullStory setVars failed:', err);
    }
  }

  public trackError(
    errorType: string,
    error: Error | string,
    context?: TrackData
  ): void {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const stackTrace = typeof error === 'string' ? undefined : error.stack;

    this.event(errorType as unknown as FullStoryEvent, {
      error_message: errorMessage,
      stack_trace: stackTrace,
      ...context,
    });
  }

  public identify(uid: string, userVars?: TrackData): void {
    if (!this.initialized || !this.consentGiven) return;
    try {
      FullStory.identify(uid, userVars);
    } catch (err) {
      log.error('FullStory identify failed:', err);
    }
  }
}
